import { applyCardAnimationStyle } from "@theme/helpers";

const STORAGE_KEY = "viewedProducts";
const SECTION_SELECTOR = "[data-recommended-recently-viewed-section]";
const SLIDER_SELECTOR = "[data-recommended-recently-viewed-slider]";
const TRACK_SELECTOR = ".blaze-track";
const TAB_TRIGGER_SELECTOR = "[data-recommended-recently-viewed-tab-trigger]";
const TAB_PANEL_SELECTOR = "[data-recommended-recently-viewed-tab-panel]";
const TAB_NAV_SELECTOR = "[data-recommended-recently-viewed-tab-nav]";
const RECOMMENDATIONS_WRAPPER_SELECTOR = "[data-recommended-recently-viewed-recommendations]";
const ROOT_MARGIN = "500px";

const getStoredHandles = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    console.warn("Recently viewed storage unavailable", error);
    return null;
  }
};

const parseHandles = (value) => {
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed.map((handle) => String(handle).trim()).filter(Boolean);
    }
  } catch (error) {
    // fall through to string parsing
  }

  return value
    .replace(/[\[\]]/g, "")
    .split(",")
    .map((handle) => handle.replace(/"/g, "").trim())
    .filter(Boolean);
};

const dedupe = (items) => {
  const seen = new Set();
  const result = [];
  items.forEach((item) => {
    if (seen.has(item)) return;
    seen.add(item);
    result.push(item);
  });
  return result;
};

const waitForBlazeInstance = (sliderEl) => {
  if (!sliderEl) return Promise.resolve(null);

  return new Promise((resolve) => {
    if (sliderEl.__themeBlazeSlider) {
      resolve(sliderEl.__themeBlazeSlider);
      return;
    }

    const check = () => {
      if (sliderEl.__themeBlazeSlider) {
        resolve(sliderEl.__themeBlazeSlider);
        return;
      }
      requestAnimationFrame(check);
    };

    check();
  });
};

const fetchProductCard = async (handle) => {
  const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : "/";
  const url = `${root}products/${encodeURIComponent(handle)}?section_id=product-card`;
  const response = await fetch(url, { credentials: "same-origin" });

  if (!response.ok) {
    throw new Error(`Section render failed with status ${response.status}`);
  }

  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, "text/html");
  const productCard = doc.querySelector("product-card");
  if (!productCard) return null;

  const productId = productCard.getAttribute("data-product-id");
  if (productId === null || productId === "") return null;

  return productCard.outerHTML;
};

class RecommendationsLoader {
  constructor(config = ".recommended-products-wrapper") {
    if (typeof config === "string") {
      this.section = document.querySelector(config);
      this.onLoaded = null;
      this.onEmpty = null;
      this.onError = null;
      this.onIntersect = null;
    } else {
      this.section = config?.element || null;
      this.onLoaded = typeof config?.onLoaded === "function" ? config.onLoaded : null;
      this.onEmpty = typeof config?.onEmpty === "function" ? config.onEmpty : null;
      this.onError = typeof config?.onError === "function" ? config.onError : null;
      this.onIntersect = typeof config?.onIntersect === "function" ? config.onIntersect : null;
    }

    this.observer = null;
    this.observerTarget = null;
    this.hasLoaded = false;
    this.isLoading = false;
    this.fallbackTimer = null;

    if (this.section) {
      this.observerTarget = this.resolveObserverTarget();
      this.init();
    }
  }

  resolveObserverTarget() {
    return (
      this.section.closest("[data-recommendations-observe]") ||
      this.section.closest(".product-recommendations") ||
      this.section.parentElement ||
      this.section
    );
  }

  init() {
    const target = this.observerTarget || this.section;
    if (!target) return;

    if (typeof window.IntersectionObserver !== "function") {
      this.loadRecommendations();
      return;
    }

    this.observer = new IntersectionObserver((entries) => this.handleIntersection(entries), {
      rootMargin: "400px",
    });

    this.observer.observe(target);

    if (target.getBoundingClientRect().height === 0) {
      requestAnimationFrame(() => {
        if (!this.hasLoaded) this.loadRecommendations();
      });
    }

    this.fallbackTimer = window.setTimeout(() => {
      if (!this.hasLoaded) this.loadRecommendations();
    }, 4000);
  }

  handleIntersection(entries) {
    if (!Array.isArray(entries)) return;
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      if (this.onIntersect) this.onIntersect();
      this.observer?.unobserve(entry.target);
      this.loadRecommendations();
    });
  }

  async loadRecommendations() {
    if (this.isLoading || this.hasLoaded) return;

    const url = this.section.dataset.url;
    if (!url) {
      this.hasLoaded = true;
      return;
    }

    this.isLoading = true;

    try {
      const response = await fetch(url, { credentials: "same-origin" });

      if (response.status === 401) {
        this.onNoRecommendations();
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const text = await response.text();
      const html = document.createElement("div");
      html.innerHTML = text;

      let recommendations = html.querySelector(".recommended-products-wrapper");
      if (!recommendations) {
        recommendations = html.querySelector(".product-recommendations");
      }

      if (recommendations && recommendations.innerHTML.trim().length) {
        const embeddedHeader = recommendations.querySelector(".section-header");
        if (embeddedHeader) {
          embeddedHeader.remove();
        }

        const embeddedNavs = recommendations.querySelectorAll(".blaze-navigation-arrows");
        embeddedNavs.forEach((nav) => nav.remove());

        this.section.innerHTML = recommendations.innerHTML;
        this.onRecommendationsLoaded();
      } else {
        this.onNoRecommendations();
      }
    } catch (error) {
      this.onLoadError(error);
    } finally {
      if (this.fallbackTimer) {
        clearTimeout(this.fallbackTimer);
        this.fallbackTimer = null;
      }
      this.isLoading = false;
      this.hasLoaded = true;
    }
  }

  onRecommendationsLoaded() {
    const slider = this.section.querySelector("[data-blaze-slider]");
    if (!slider) {
      if (this.onLoaded) this.onLoaded(this.section);
      return;
    }

    const sectionScope = slider.closest('[id^="shopify-section-"]');
    if (sectionScope?.id) {
      slider.dataset.sliderNavScope = `#${sectionScope.id}`;
    }
    if (this.section.dataset.sliderNavNext) {
      slider.dataset.sliderNavNext = this.section.dataset.sliderNavNext;
    } else if (!slider.dataset.sliderNavNext) {
      slider.dataset.sliderNavNext = ".blaze-button-next";
    }
    if (this.section.dataset.sliderNavPrev) {
      slider.dataset.sliderNavPrev = this.section.dataset.sliderNavPrev;
    } else if (!slider.dataset.sliderNavPrev) {
      slider.dataset.sliderNavPrev = ".blaze-button-prev";
    }
    slider.dataset.sliderLoop = "false";

    // Force dataset-driven config for this slider so loop=false is guaranteed.
    // data-blaze-config may omit loop keys, which otherwise default to true.
    slider.removeAttribute("data-blaze-config");

    const controller = window.themeBlazeSliderController;
    if (controller && typeof controller.init === "function") {
      // If global auto-init already created an instance, re-init so config changes apply.
      if (typeof controller.destroy === "function" && slider.__themeBlazeSlider) {
        controller.destroy(slider);
      }
      controller.init(slider);
    }

    if (this.onLoaded) {
      this.onLoaded(this.section);
    }
  }

  onNoRecommendations() {
    if (this.onEmpty) this.onEmpty(this.section);
  }

  onLoadError(error) {
    if (this.onError) this.onError(error, this.section);
  }
}

class RecommendedRecentlyViewedSection {
  constructor(section) {
    this.section = section;
    this.slider = section.querySelector(SLIDER_SELECTOR);
    this.track = this.slider?.querySelector(TRACK_SELECTOR) || null;
    this.maxProducts = Number(section.dataset.maxProducts || 12);
    this.animationStyle = section.dataset.animationStyle || "none";
    this.tabTriggers = Array.from(section.querySelectorAll(TAB_TRIGGER_SELECTOR));
    this.tabPanels = Array.from(section.querySelectorAll(TAB_PANEL_SELECTOR));
    this.tabNavs = Array.from(section.querySelectorAll(TAB_NAV_SELECTOR));
    this.recommendationsWrapper = section.querySelector(RECOMMENDATIONS_WRAPPER_SELECTOR);

    this.availableTabs = {
      recommendations: Boolean(this.recommendationsWrapper?.dataset?.url),
      "recently-viewed": true,
    };
    const swapTabs = this.section.dataset.swapTabs === "true";
    this.activeTab = swapTabs ? "recently-viewed" : (this.availableTabs.recommendations ? "recommendations" : "recently-viewed");

    if (!this.slider || !this.track) return;

    this.bindTabs();
    this.initRecommendations();
    this.activateTab(this.activeTab);

    const stored = getStoredHandles();
    const handles = dedupe(parseHandles(stored)).slice(-this.maxProducts).reverse();
    if (!handles.length) {
      this.handleRecentlyViewedEmpty();
      return;
    }

    this.handles = handles;

    if (this.activeTab === "recently-viewed") {
      this.preloadRecentlyViewed();
    }
  }

  bindTabs() {
    this.tabTriggers.forEach((trigger) => {
      trigger.addEventListener("click", () => {
        const nextTab = trigger.dataset.tabTarget;
        if (!nextTab || this.availableTabs[nextTab] === false) return;
        this.activateTab(nextTab);
      });
    });
  }

  initRecommendations() {
    if (!this.recommendationsWrapper || !this.availableTabs.recommendations) {
      this.availableTabs.recommendations = false;
      this.syncTabVisibility();
      this.preloadRecentlyViewed();
      return;
    }

    this.recommendations = new RecommendationsLoader({
      element: this.recommendationsWrapper,
      onIntersect: () => {
        this.preloadRecentlyViewed();
      },
      onLoaded: () => {
        this.availableTabs.recommendations = true;
        this.syncTabVisibility();
        if (this.activeTab === "recommendations") this.rebindSliderNavigation("recommendations");
      },
      onEmpty: () => {
        this.availableTabs.recommendations = false;
        this.syncTabVisibility();
      },
      onError: () => {
        this.availableTabs.recommendations = false;
        this.syncTabVisibility();
      },
    });
  }

  syncTabVisibility() {
    this.tabTriggers.forEach((trigger) => {
      const tabId = trigger.dataset.tabTarget;
      const isVisible = this.availableTabs[tabId] !== false;
      trigger.hidden = !isVisible;
    });

    this.tabPanels.forEach((panel) => {
      const tabId = panel.dataset.recommendedRecentlyViewedTabPanel;
      if (!tabId || this.availableTabs[tabId] !== false) return;
      panel.hidden = true;
      panel.setAttribute("aria-hidden", "true");
    });

    this.tabNavs.forEach((nav) => {
      const tabId = nav.dataset.recommendedRecentlyViewedTabNav;
      if (!tabId) return;
      const isAvailable = this.availableTabs[tabId] !== false;
      const isActive = tabId === this.activeTab;
      nav.hidden = !isAvailable || !isActive;
      nav.setAttribute("aria-hidden", isAvailable && isActive ? "false" : "true");
    });

    if (this.availableTabs[this.activeTab] === false) {
      const fallbackTab = this.availableTabs.recommendations ? "recommendations" : "recently-viewed";
      if (this.availableTabs[fallbackTab] === false) {
        this.section.hidden = true;
        return;
      }
      this.activateTab(fallbackTab);
    }
  }

  activateTab(tabId) {
    if (this.availableTabs[tabId] === false) return;
    this.activeTab = tabId;

    this.tabTriggers.forEach((trigger) => {
      const isActive = trigger.dataset.tabTarget === tabId;
      trigger.classList.toggle("active", isActive);
      trigger.classList.toggle("bold", isActive);
      trigger.classList.toggle("regular", !isActive);
      trigger.setAttribute("aria-selected", isActive ? "true" : "false");
      trigger.tabIndex = isActive ? 0 : -1;
    });

    this.tabPanels.forEach((panel) => {
      const isActive = panel.dataset.recommendedRecentlyViewedTabPanel === tabId;
      panel.hidden = !isActive;
      panel.setAttribute("aria-hidden", isActive ? "false" : "true");
    });

    this.tabNavs.forEach((nav) => {
      const isActive = nav.dataset.recommendedRecentlyViewedTabNav === tabId;
      nav.hidden = !isActive;
      nav.setAttribute("aria-hidden", isActive ? "false" : "true");
    });

    this.rebindSliderNavigation(tabId);
  }

  rebindSliderNavigation(tabId) {
    const panel = this.tabPanels.find(
      (tabPanel) => tabPanel.dataset.recommendedRecentlyViewedTabPanel === tabId,
    );
    if (!panel) return;

    const slider = panel.querySelector("[data-blaze-slider]");
    const controller = window.themeBlazeSliderController;
    if (!slider || !controller || typeof controller.init !== "function") return;

    if (typeof controller.destroy === "function" && slider.__themeBlazeSlider) {
      controller.destroy(slider);
    }
    controller.init(slider);
  }

  preloadRecentlyViewed() {
    if (this.hasLoadedRecentlyViewed || this.isLoadingRecentlyViewed) return;

    this.slider.setAttribute("hidden", "hidden");
    this.slider.setAttribute("aria-busy", "true");

    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(() => {
        this.loadRecentlyViewed();
      });
      return;
    }

    window.setTimeout(() => {
      this.loadRecentlyViewed();
    }, 0);
  }

  async loadRecentlyViewed() {
    if (this.isLoadingRecentlyViewed || this.hasLoadedRecentlyViewed) {
      return;
    }
    this.isLoadingRecentlyViewed = true;

    let appended = 0;
    const fragment = document.createDocumentFragment();

    for (const handle of this.handles) {
      try {
        const markup = await fetchProductCard(handle);
        if (!markup) continue;

        const slide = document.createElement("div");
        slide.classList.add("h-auto");
        slide.innerHTML = markup;
        applyCardAnimationStyle(slide, this.animationStyle);
        fragment.appendChild(slide);
        appended += 1;
      } catch (error) {
        console.warn(`Recently viewed product failed for handle "${handle}"`, error);
      }
    }

    if (appended > 0) {
      this.track.appendChild(fragment);
    }

    if (appended === 0) {
      this.handleRecentlyViewedEmpty();
      this.isLoadingRecentlyViewed = false;
      this.hasLoadedRecentlyViewed = true;
      return;
    }

    this.slider.classList.remove("blaze-loading");
    this.slider.removeAttribute("hidden");
    this.slider.removeAttribute("aria-busy");

    const minSlidesForLoop = Number(this.slider.dataset.minSlidesForLoop || 2);
    if (this.slider.dataset.sliderLoop === "true" && appended < minSlidesForLoop) {
      this.slider.dataset.sliderLoop = "false";
    }

    const controller = window.themeBlazeSliderController;
    if (controller && typeof controller.refresh === "function") {
      controller.refresh(this.slider);
    }

    const blaze = await waitForBlazeInstance(this.slider);
    if (blaze && typeof blaze.refresh === "function") {
      blaze.refresh();
    }
    this.isLoadingRecentlyViewed = false;
    this.hasLoadedRecentlyViewed = true;
  }

  handleRecentlyViewedEmpty() {
    this.availableTabs["recently-viewed"] = false;
    this.syncTabVisibility();

    this.slider.classList.remove("blaze-loading");
    this.slider.setAttribute("hidden", "hidden");
    this.slider.removeAttribute("aria-busy");
  }
}

const initRecommendedRecentlyViewed = () => {
  const sections = document.querySelectorAll(SECTION_SELECTOR);
  sections.forEach((section) => new RecommendedRecentlyViewedSection(section));
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initRecommendedRecentlyViewed);
} else {
  initRecommendedRecentlyViewed();
}
